import { get } from "lodash";
import {
  all,
  call,
  put,
  select,
  takeEvery,
  takeLatest,
  getContext,
} from "redux-saga/effects";

import type { PostApplicationCreateRequestBody } from "@superblocksteam/schemas";
import {
  getAiQuotaPaywallReasonFromMessage,
  getDefaultPageLayout,
  AccessMode,
} from "@superblocksteam/shared";
import type {
  Agent,
  AiQuotaPaywallReason,
  PutApplicationSettingsUpdateResponseBody,
} from "@superblocksteam/shared";

import { queueCloneDatabaseSetup } from "core/clark/cloneDatabaseSetup";
import { NOOP } from "core/utils/function";
import { getEditorBasePath } from "hooks/store/useGetEditorPath";
import { openAiQuotaPaywallModal } from "legacy/actions/modalActions";
import type { ApiResponse } from "legacy/api/ApiResponses";
import ApplicationApi from "legacy/api/ApplicationApi";
import type { UpdateNonVersionedApplicationSettingsRequest } from "legacy/api/ApplicationApi";
import { makeSingleUseSocketConnection } from "legacy/api/ISocketRPC";
import type { StdISocketRPCClient } from "legacy/api/ISocketRPC";
import { Variant } from "legacy/components/ads/common";
import { Toaster } from "legacy/components/ads/Toast";
import { ERROR_CODES } from "legacy/constants/ApiConstants";
import {
  DELETING_APPLICATION,
  DUPLICATING_APPLICATION,
} from "legacy/constants/messages";
import {
  ReduxActionErrorTypes,
  ReduxActionTypes,
} from "legacy/constants/ReduxActionConstants";
import type { ReduxAction } from "legacy/constants/ReduxActionConstants";
import { EditorRoute } from "legacy/constants/routes";
import type { Profiles } from "legacy/reducers/entityReducers/appReducer";
import { getAppProfilesInCurrentMode } from "legacy/selectors/applicationSelectors";
import {
  getCurrentApplicationId,
  getCurrentPageId,
} from "legacy/selectors/editorSelectors";
import selectLastSuccessfulWrite from "legacy/selectors/successfulWriteSelector";
import { getAccessMode } from "legacy/selectors/usersSelectors";
import { BASE_THEME_NEW_APPS } from "legacy/themes/constants";
import { getOpaAgents } from "legacy/utils/getOpaAgents";
import { lock as lockApis } from "store/slices/apisShared/sharedPersistApiLock";
import { getAllV2ApisSaga } from "store/slices/apisV2";
import {
  fetchApplicationPermissions,
  fetchApplicationPermissionsError,
  fetchApplicationPermissionsSuccess,
  updateApplicationMetadata,
  updateApplicationSettings,
  updateNonVersionedApplicationSettings,
} from "store/slices/application/applicationActions";
import { getApplicationPermissions } from "store/slices/application/client";
import { Flag, selectFlagById, selectFlags } from "store/slices/featureFlags";
import {
  createApplicationError,
  createApplicationInit,
  createApplicationSuccess,
  deleteApplicationError,
  deleteApplicationInit,
  deleteApplicationSuccess,
  duplicateApplicationError,
  duplicateApplicationInit,
  duplicateApplicationSuccess,
  fetchAllApplicationsError,
  getAllApplicationsInit,
  getAllApplicationsSuccess,
} from "store/slices/homepage/slice";
import { selectOnlyOrganization } from "store/slices/organizations";
import { orgIsOnPremise } from "store/slices/organizations/utils";
import { callSagas } from "store/utils/saga";
import { sendErrorUINotification } from "utils/notification";
import { getShouldSignAndVerify } from "utils/resource-signing";

import { bestCountryToLaunchCSB } from "../utils/CsbLaunchRegion";
import { updateProfiles } from "./ApplicationSagaHelpers";
import { validateResponse } from "./ErrorSagas";

const getAiQuotaErrorMessage = (error: unknown): string => {
  const messages = [
    error instanceof Error ? error.message : undefined,
    typeof error === "string" ? error : undefined,
    get(error, "message"),
    get(error, "response.data.responseMeta.error.reason"),
    get(error, "responseMeta.error.reason"),
    get(error, "data.responseMeta.error.reason"),
    get(error, "response.data.responseMeta.error.message"),
    get(error, "responseMeta.error.message"),
    get(error, "data.responseMeta.error.message"),
    get(error, "response.data.message"),
    get(error, "data.message"),
  ].filter((value): value is string => Boolean(value));

  return messages.join(" ");
};

const getAiQuotaPaywallReason = (
  error: unknown,
): AiQuotaPaywallReason | undefined => {
  return getAiQuotaPaywallReasonFromMessage(getAiQuotaErrorMessage(error));
};

function* getAllApplicationSaga(): Generator<any, any, any> {
  try {
    const response: Awaited<
      ReturnType<typeof ApplicationApi.getAllApplication>
    > = yield call(ApplicationApi.getAllApplication);
    const isValidResponse = yield validateResponse(response);
    if (isValidResponse) {
      yield put(getAllApplicationsSuccess(response.data.applications));
    }
  } catch (error: any) {
    yield put(fetchAllApplicationsError(error.message as string));
  }
}

function* updateNonVersionedApplicationSettingsSaga(
  action: ReturnType<typeof updateNonVersionedApplicationSettings>,
): Generator<any, any, any> {
  let unlock = NOOP;
  const currentApplicationId: string = yield select(getCurrentApplicationId);
  const currentPageId: string = yield select(getCurrentPageId);
  let shouldRefetchApis = false;
  try {
    // TODO: this lock is not always useful since the saga is put in takeLatest, it will be cancelled if new task comes in and lock will be released while the response it not received.
    unlock = yield call(lockApis, action.payload.id);
    const lastSuccessfulWrite: number = yield select(selectLastSuccessfulWrite);

    const request: UpdateNonVersionedApplicationSettingsRequest = {
      ...action.payload,
      lastSuccessfulWrite,
    };
    // update settings redux state optimistically

    // this block will only run in editor mode
    yield put(updateApplicationSettings(request.settings));
    const appProfiles: Profiles = yield select(getAppProfilesInCurrentMode);
    // this could be undefined if old default profile is deleted
    const oldDefaultId = appProfiles.default?.id;
    // start updating the profiles that used to generate global profiles obj
    yield call(updateProfiles);

    shouldRefetchApis =
      oldDefaultId !== request.settings?.profiles?.editor.defaultProfileId;

    const superblocksSupportUpdateEnabled: boolean = yield select(
      selectFlagById,
      Flag.ENABLE_SUPERBLOCKS_SUPPORT_UPDATES,
    );
    const response: ApiResponse<PutApplicationSettingsUpdateResponseBody> =
      yield call(
        ApplicationApi.updateNonVersionedApplicationSettings,
        request,
        superblocksSupportUpdateEnabled,
      );
    const isValidResponse = yield validateResponse(response);
    if (isValidResponse && request) {
      yield put({
        type: ReduxActionTypes.UPDATE_APPLICATION_SUCCESS,
      });
    }

    yield put({
      type: ReduxActionTypes.UPDATE_LAST_SUCCESSFUL_WRITE,
      payload: response.data.updated,
    });
    if (shouldRefetchApis) {
      //refetch apis after default profile changes
      yield callSagas(
        [
          getAllV2ApisSaga.apply({
            applicationId: currentApplicationId,
            pageId: currentPageId,
          }),
        ],
        { throwOnFailure: true },
      );
    }
  } catch (error) {
    yield put({
      type: ReduxActionErrorTypes.UPDATE_APPLICATION_ERROR,
      payload: {
        error,
      },
    });
  } finally {
    unlock();
  }
}

function* updateApplicationMetadataSaga(
  action: ReturnType<typeof updateApplicationMetadata>,
): Generator<any, any, any> {
  const currentApplicationId: string = yield select(getCurrentApplicationId);
  try {
    const { disableNotification, ...request } = action.payload;

    const superblocksSupportUpdateEnabled: boolean = yield select(
      selectFlagById,
      Flag.ENABLE_SUPERBLOCKS_SUPPORT_UPDATES,
    );
    const response: Awaited<
      ReturnType<typeof ApplicationApi.updateApplicationMetadata>
    > = yield call(
      ApplicationApi.updateApplicationMetadata,
      request,
      superblocksSupportUpdateEnabled,
    );
    const isValidResponse = yield validateResponse(response);
    if (isValidResponse && request) {
      if (request.name && !disableNotification) {
        // TODO: do we need to refactor the rename notification here?
        Toaster.show({
          text: "Application name updated",
          variant: Variant.success,
        });
      }
      yield put({
        type: ReduxActionTypes.UPDATE_APPLICATION_SUCCESS,
      });
    }
    if (isValidResponse && request.id === currentApplicationId) {
      if (request.name) {
        yield put({
          type: ReduxActionTypes.CURRENT_APPLICATION_NAME_UPDATE,
          payload: request.name,
        });
      }
      if (
        request.isPublic != null &&
        request.isPublic === response.data?.isPublic
      ) {
        yield put({
          type: ReduxActionTypes.CURRENT_APPLICATION_IS_PUBLIC_UPDATE,
          payload: response.data.isPublic,
        });
      }
    }
    yield put({
      type: ReduxActionTypes.UPDATE_LAST_SUCCESSFUL_WRITE,
      payload: response.data.updated,
    });
  } catch (error) {
    yield put({
      type: ReduxActionErrorTypes.UPDATE_APPLICATION_ERROR,
      payload: {
        error,
      },
    });
  }
}

function* deleteApplicationSaga(
  action: ReturnType<typeof deleteApplicationInit>,
): Generator<any, any, any> {
  try {
    Toaster.show({
      text: DELETING_APPLICATION,
    });
    const request = action.payload;
    const response: Awaited<
      ReturnType<typeof ApplicationApi.deleteApplication>
    > = yield call(ApplicationApi.deleteApplication, request);
    const isValidResponse = yield validateResponse(response);
    if (isValidResponse) {
      yield put(deleteApplicationSuccess({ id: (response.data as any).id }));
    }
  } catch (error) {
    yield put(deleteApplicationError());
    const message = `Could not delete application: ${
      "message" in (error as any) ? (error as any).message : error
    }`;
    sendErrorUINotification({ message });
  }
}

function* duplicateApplicationSaga(
  action: ReturnType<typeof duplicateApplicationInit>,
): Generator<any, any, any> {
  try {
    Toaster.show({
      text: DUPLICATING_APPLICATION,
    });
    const request = action.payload;
    // If using OPA, use the RPC code path
    let rpcClient: undefined | StdISocketRPCClient;
    const organization: ReturnType<typeof selectOnlyOrganization> =
      yield select(selectOnlyOrganization);
    if (orgIsOnPremise(organization)) {
      const agents: Agent[] = yield call(getOpaAgents);
      const shouldUseWs: boolean = yield call(getShouldSignAndVerify, agents);
      if (shouldUseWs) {
        if (agents.length === 0) {
          yield put(duplicateApplicationError());
          yield put({
            type: ReduxActionTypes.SAFE_CRASH_SUPERBLOCKS_REQUEST,
            payload: {
              code: ERROR_CODES.NO_AGENTS,
            },
          });
          return;
        }
        rpcClient = yield call(
          makeSingleUseSocketConnection,
          agents,
          organization,
        );
      }
    }
    let response: Awaited<ReturnType<typeof ApplicationApi.cloneApplication>>;
    try {
      response = rpcClient
        ? yield call(rpcClient.call.v2.application.clone, request)
        : yield call(ApplicationApi.cloneApplication, request);
    } finally {
      rpcClient?.close();
    }
    const isValidResponse = yield validateResponse(response);
    if (isValidResponse) {
      const application = response.data;
      yield put(duplicateApplicationSuccess());

      const isCodeModeEnabled: boolean = response.data.devEnvEnabled ?? false;

      // Must happen before the navigation below: the editor loads in a fresh
      // page and reads this back out of localStorage. `?clarkPrompt=` is how
      // this tab claims its own queued entry.
      const clarkPrompt = queueCloneDatabaseSetup(application);

      const pageURL = getEditorBasePath(EditorRoute.EditApplication, {
        applicationId: application.id,
        params: clarkPrompt ? { clarkPrompt } : {},
      });
      if (isCodeModeEnabled) {
        window.location.href = `/code-mode${pageURL}`;
      } else {
        const navigate = yield getContext("navigate");
        navigate(pageURL);
      }
    }
  } catch (error) {
    yield put(duplicateApplicationError());
    const message = `Could not clone application: ${
      "message" in (error as any) ? (error as any).message : error
    }`;
    sendErrorUINotification({ message });
  }
}

function* createApplicationSaga(
  action: ReturnType<typeof createApplicationInit>,
): Generator<any, any, any> {
  const {
    applicationName,
    folderId,
    orgId,
    createUsingReact,
    templateName,
    initialPrompt,
    initialAttachments,
    initialContextEntities,
    initialImportSource,
  } = action.payload;
  const featureFlags: ReturnType<typeof selectFlags> =
    yield select(selectFlags);

  try {
    const request: PostApplicationCreateRequestBody = {
      name: applicationName,
      folderId,
      organizationId: orgId,
      settings: {
        theme: BASE_THEME_NEW_APPS,
      },
      ipCountry: bestCountryToLaunchCSB(),
      initialPrompt,
    };

    if (createUsingReact) {
      request.createUsingReact = true;
      if (templateName) {
        request.templateName = templateName;
      }
    } else {
      request.initialPageDSL = yield call(
        getDefaultPageLayout,
        8,
        featureFlags,
      );
    }

    // If signing is required, use the RPC code path
    let rpcClient: undefined | StdISocketRPCClient;
    const organization: ReturnType<typeof selectOnlyOrganization> =
      yield select(selectOnlyOrganization);
    if (orgIsOnPremise(organization)) {
      const agents: Agent[] = yield call(getOpaAgents);
      const shouldSign: boolean = yield call(getShouldSignAndVerify, agents);
      if (shouldSign) {
        if (agents.length === 0) {
          throw new Error("Could not sign application, no OPA agents found");
        }
        rpcClient = yield call(
          makeSingleUseSocketConnection,
          agents,
          organization,
        );
      }
    }
    let response: Awaited<ReturnType<typeof ApplicationApi.createApplication>>;
    try {
      response = rpcClient
        ? yield call(rpcClient.call.v2.application.create, request)
        : yield call(ApplicationApi.createApplication, request);
    } finally {
      rpcClient?.close();
    }
    const isValidResponse = yield validateResponse(response);
    if (isValidResponse) {
      const application = response.data;
      yield put(createApplicationSuccess(application));
      yield put({
        type: ReduxActionTypes.UPDATE_APPLICATION_HASH_TREE,
        payload: { tree: application.signature?.root },
      });

      // Store the initial prompt (and optional uploaded attachments) for Clark AI
      // to pick up after code-mode loads. AiCanvasEmptyState reads this key on
      // mount and dispatches it as a normal user message + aiGenerate.
      if (initialPrompt) {
        try {
          sessionStorage.setItem(
            `clark-ai-homepage-prompt:${application.id}`,
            JSON.stringify({
              text: initialPrompt,
              attachments: initialAttachments ?? [],
              contextEntities: initialContextEntities ?? [],
              importSource: initialImportSource,
            }),
          );
        } catch {
          // sessionStorage may be unavailable
        }
      }

      const isCodeModeEnabled = response.data.devEnvEnabled ?? false;
      const pageURL = getEditorBasePath(EditorRoute.EditApplication, {
        applicationId: application.id,
      });
      if (isCodeModeEnabled) {
        window.location.href = `/code-mode${pageURL}`;
      } else {
        const navigate = yield getContext("navigate");
        navigate(pageURL, action.payload.navigateOpts);
      }
    }
  } catch (error: any) {
    const aiQuotaPaywallReason = getAiQuotaPaywallReason(error);
    if (aiQuotaPaywallReason) {
      yield put(openAiQuotaPaywallModal(aiQuotaPaywallReason));
      yield put(createApplicationError());
      return;
    }
    sendErrorUINotification({
      message: "Could not create application",
      description: error.message,
    });
    yield put(createApplicationError());
  }
}

function* setProfileSaga(
  action: ReduxAction<{
    profileId: string;
  }>,
): Generator<any, any, any> {
  yield updateProfiles(action.payload.profileId);
}

function* fetchApplicationPermissionsSaga(
  action: ReturnType<typeof fetchApplicationPermissions>,
): Generator<any, any, any> {
  const accessMode: ReturnType<typeof getAccessMode> =
    yield select(getAccessMode);
  if (
    accessMode === AccessMode.EXTERNAL_USER ||
    accessMode === AccessMode.VISITOR
  ) {
    return;
  }

  const applicationId = action.payload.applicationId;
  try {
    const response = yield call(getApplicationPermissions, { applicationId });
    if (response) {
      yield put(fetchApplicationPermissionsSuccess(response.permissions));
    } else {
      yield put(
        fetchApplicationPermissionsError(
          "Failed to fetch application permissions",
        ),
      );
    }
  } catch (e: any) {
    yield put(fetchApplicationPermissionsError(e?.message ?? ""));
  }
}

export default function* applicationSagas() {
  yield all([
    takeLatest(
      updateNonVersionedApplicationSettings.type,
      updateNonVersionedApplicationSettingsSaga,
    ),
    takeEvery(updateApplicationMetadata.type, updateApplicationMetadataSaga),
    takeLatest(getAllApplicationsInit.type, getAllApplicationSaga),
    takeLatest(createApplicationInit.type, createApplicationSaga),
    takeLatest(deleteApplicationInit.type, deleteApplicationSaga),
    takeLatest(duplicateApplicationInit.type, duplicateApplicationSaga),
    takeLatest(ReduxActionTypes.SET_PROFILE, setProfileSaga),
    takeLatest(
      fetchApplicationPermissions.type,
      fetchApplicationPermissionsSaga,
    ),
  ]);
}
